import { useEffect } from "react"
import Sticky from "react-stickynode"
import { useTranslation } from "next-i18next"
import { serverSideTranslations } from "next-i18next/serverSideTranslations"
import dynamic from "next/dynamic"
import MetaData from "@/common/components/MetaData"
import SectionLine from "@/common/components/_UI/SectionLine"

import GlobalExchange from "@/common/assets/image/solutions/web3-fund-management/global-exchange.svg"
import LowTransactionFees from "@/common/assets/image/solutions/web3-fund-management/low-transaction-fees.svg"
import Secure from "@/common/assets/image/solutions/web3-fund-management/secure.svg"
import Stablecoin from "@/common/assets/image/solutions/web3-fund-management/stablecoin.svg"
import featureBanner2 from "@/common/assets/image/assetPro/featureBanner/image7.avif"
import guardMoney from "@/common/assets/image/solutions/web3-fund-management/guardMoney.svg"
import paymentFakeCover from "@/common/assets/image/solutions/web3-fund-management/payment-fake-cover.avif"
import blogFakeCover from "@/common/assets/image/solutions/web3-fund-management/blog-fake-cover.avif"

const NavbarNew = dynamic(import("@/containers/common/NavbarNew"))

import { DarkBGWrapper, LayoutWrapper } from "@/common/components/LayoutStyle"
import Description, {
  CardWrapper,
  SubDescContent,
  SubDescLayoutWrapper,
  SubDescWrapper,
  type SubDescription
} from "@/common/components/Description"
import { CardWrapper as ProductCardWrapper } from "@/common/components/ProductCard/Card"
import { ProductBanner } from "@/common/components/ProductBanner"
import {
  ProductNames,
  SolutionNames,
  SolutionPlans
} from "@/common/data/Products"
import Footer from "@/containers/common/FooterNew"
import { useRouter } from "next/router"
import { Web3FundManagementCardList } from "@/common/components/ProductCard/Web3FundManagementCardList"
import NextImg from "@/common/components/NextImg"
import TrustedBy from "@/common/components/TrustedBy"
import LazyLoadComponent from "@/common/components/LazyLoadComponent"
import styled from "styled-components"
import { BGWrapper, theme } from "@/common/Mixin"
import { assoc, pipe } from "lodash/fp"
import { ContentWrapper } from "@/containers/common/style"
import { EventCardWrapper } from "../events"
import MediumArticleCard from "@/common/components/MediumArticleCard"
import Button from "@/common/components/_UI/ButtonNew"
import { ButtonWrapper } from "@/common/components/Introduction"

const Web3FundManagement = () => {
  const { t } = useTranslation(["web3FundManagement", "common"])
  const router = useRouter()

  useEffect(() => {
    const { plan } = router.query
    if (plan != null) {
      setTimeout(() => {
        document.getElementById("contactUsSection")?.scrollIntoView({
          behavior: "smooth",
          block: "start"
        })
      }, 200)
    }
  })

  return (
    <Web3FundManagementWrapper>
      <MetaData
        title={t("meta.title")}
        description={t("meta.description")}
        url={t("meta.url")}
        image="https://storage.googleapis.com/kryptogo-official-website-public/og-logo.webp"
        languageAlternates={[
          {
            hrefLang: "en-US",
            href: "https://www.kryptogo.com/solutions/web3-fund-management"
          },
          {
            hrefLang: "zh-TW",
            href: "https://www.kryptogo.com/tw/solutions/web3-fund-management"
          },
          {
            hrefLang: "zh-CN",
            href: "https://www.kryptogo.com/cn/solutions/web3-fund-management"
          },
          {
            hrefLang: "ja-JP",
            href: "https://www.kryptogo.com/ja/solutions/web3-fund-management"
          }
        ]}
      />

      <Sticky top={0} innerZ={1000} activeClass="sticky-active">
        <NavbarNew
          contactOnClick={e => {
            e.preventDefault()
            document.getElementById("contactUsSection")?.scrollIntoView({
              behavior: "smooth",
              block: "start"
            })
          }}
        />
      </Sticky>
      <DarkBGWrapper></DarkBGWrapper>
      <LayoutWrapper>
        <ProductBanner
          style={{ position: "unset" }}
          icon={guardMoney}
          bannerType={SolutionNames.Web3FundManagement}
          title={t("Banner.productName")}
          titleh1={t("Banner.title")}
          description={t("Banner.description")}
          primaryButton={{
            content: t("Banner.button1"),
            arrowType: "none",
            href: "#contact-us",
            target: "_self"
          }}
          subtleButton={{
            content: t("Banner.button2"),
            arrowType: "none",
            href: "#features",
            target: "_self"
          }}
        />
        <SectionLine style={{ marginBottom: "-60px" }} />
        <div id="features" />
        <Description
          backgroundType="envelope"
          items={[
            {
              titles: [
                {
                  text: t("Desc.1.title"),
                  textType: "h4",
                  textClass: "primary"
                },
                {
                  text: t("Desc.1.description"),
                  textType: "h3"
                }
              ],
              subDescriptions: [
                {
                  icon: Stablecoin,
                  title: t("Desc.1.subDesc.0.title"),
                  content: t("Desc.1.subDesc.0.description")
                },
                {
                  icon: GlobalExchange,
                  title: t("Desc.1.subDesc.1.title"),
                  content: t("Desc.1.subDesc.1.description")
                },
                {
                  icon: LowTransactionFees,
                  title: t("Desc.1.subDesc.2.title"),
                  content: t("Desc.1.subDesc.2.description")
                },
                {
                  icon: Secure,
                  title: t("Desc.1.subDesc.3.title"),
                  content: t("Desc.1.subDesc.3.description")
                }
              ].map<SubDescription>(
                pipe(
                  assoc("imageStyle", { width: "120px" }),
                  assoc("imageWrapperStyle", { margin: "1em" })
                )
              ),
              wide: true
            }
          ]}
        />
        <Description
          backgroundType="none"
          items={[
            {
              titles: [
                {
                  text: t("Desc.middle.3.title"),
                  textType: "h4",
                  textClass: "primary"
                },
                {
                  text: t("Desc.middle.3.description"),
                  textType: "h3"
                }
              ],
              wide: true
            }
          ]}
        />
        <Web3FundManagementCardList cards={["money", "defiKLine"]} />
        <Web3FundManagementCardList cards={["shoppingStore", "tether"]} />
        <SectionLine style={{ transform: "translateY(60px)" }} />
        <Description
          backgroundType="none"
          items={[
            {
              titles: [
                {
                  text: t("Desc.middle.1.title"),
                  textClass: "primary",
                  textType: "h4"
                },
                { text: t("Desc.middle.1.description"), textType: "h3" }
              ],
              wide: true
            }
          ]}
        />
        <ProductBanner
          bannerType={ProductNames.AssetPro}
          title={t("subBanner1.title")}
          titleType="primary"
          titleh1={t("subBanner1.content")}
          description={t("subBanner1.description")}
          checkList={[
            {
              list: [
                {
                  title: t("subBanner1.checkList.1.title"),
                  description: t("subBanner1.checkList.1.desc")
                },
                {
                  title: t("subBanner1.checkList.2.title"),
                  description: t("subBanner1.checkList.2.desc")
                },
                {
                  title: t("subBanner1.checkList.3.title"),
                  description: t("subBanner1.checkList.3.desc")
                },
                {
                  title: t("subBanner1.checkList.4.title"),
                  description: t("subBanner1.checkList.4.desc")
                }
              ]
            }
          ]}
          primaryButton={{
            content: t("subBanner1.cta.primary"),
            arrowType: "none",
            href: "#contact-us",
            target: "_self"
          }}
          subtleButton={{
            content: t("subBanner1.cta.subtle"),
            arrowType: "right",
            href: "/products/asset-pro",
            target: "_self"
          }}
        />
        <ProductBanner
          comp2={<NextImg alt="featureBanner3" src={featureBanner2} />}
          title={t("subBanner2.title")}
          titleType="primary"
          titleh1={t("subBanner2.content")}
          checkList={[
            {
              list: [
                {
                  title: t("subBanner2.checkList.1.title"),
                  description: t("subBanner2.checkList.1.desc")
                },
                {
                  title: t("subBanner2.checkList.2.title"),
                  description: t("subBanner2.checkList.2.desc")
                }
              ]
            }
          ]}
          description={t("subBanner2.description")}
          primaryButton={{
            content: t("subBanner2.cta.primary"),
            arrowType: "none",
            href: "#contact-us",
            target: "_self"
          }}
          subtleButton={{
            content: t("subBanner2.cta.subtle"),
            arrowType: "right",
            href: "/products/compliance-pro",
            target: "_self"
          }}
        />
        <SectionLine />
        <Description
          backgroundType="none"
          items={[
            {
              titles: [
                {
                  text: t("Desc.middle.2.title"),
                  textType: "h4",
                  textClass: "primary"
                },
                {
                  text: t("Desc.middle.2.description"),
                  textType: "h3"
                }
              ],
              wide: true
            }
          ]}
        />
        <SectionLine />
        <div id="contact-us" />
        <LazyLoadComponent
          skeletonProps={{
            width: "75%"
          }}
          props={{
            plans: [SolutionPlans.Web3FundManagement, SolutionPlans.Other],
            page: SolutionNames.Web3FundManagement,
            description: t("ContactUs.title"),
            slogan: t("ContactUs.slogan"),
            body: t("ContactUs.body"),
            style: { height: 755 }
          }}
          component={() =>
            import("@/common/components/ContactUs/ContactUsSection")
          }
        />
        <TrustedBy title={t("partners.title")} />
        <h4 style={{ margin: "auto" }}>{t("articles.title")}</h4>
        <ContentWrapper>
          <EventCardWrapper
            style={{
              width: "100%",
              gap: "5em",
              padding: "0 5em"
            }}
          >
            <MediumArticleCard
              t={t}
              img={blogFakeCover}
              title={t("blog.recommend.banner.title")}
              desc={t("blog.recommend.banner.description")}
              link="/blog/crypto-operations-challenges-solutions"
              linkText={t("button.cta-read-more", { ns: "common" })}
              linkType
            />
            <MediumArticleCard
              t={t}
              img={paymentFakeCover}
              title={t("product.assetPro.banner.title")}
              desc={t("product.assetPro.banner.description")}
              link="/products/asset-pro"
              linkText={t("button.cta-read-more", { ns: "common" })}
              linkType
            />
          </EventCardWrapper>
        </ContentWrapper>
        <div style={{ display: "flex", justifyContent: "center", gap: "1em" }}>
          <Button
            arrowType="none"
            content={t("bottom.cta.primary")}
            style={{ minWidth: "0" }}
            href="/solutions"
            target="_self"
          />
          <Button
            styleType="subtle"
            arrowType="right"
            content={t("bottom.cta.subtle")}
            style={{ minWidth: "0" }}
            href="/blog"
            target="_self"
          />
        </div>
        <Description
          items={[
            {
              titles: [{ text: t("bottom.desc.title"), textType: "h3" }],
              content: (
                <Button
                  styleType="primary"
                  arrowType="none"
                  content={t("Banner.button1")}
                  style={{ minWidth: "0", margin: "auto", marginTop: "2em" }}
                  href="/contact-us"
                  target="_self"
                />
              ),
              wide: true
            }
          ]}
          backgroundType="envelope"
        />
        <Footer />
      </LayoutWrapper>
    </Web3FundManagementWrapper>
  )
}

export const getStaticProps = async ({ locale }: { locale: string }) => ({
  props: {
    ...(await serverSideTranslations(locale, [
      "common",
      "contactUs",
      "web3FundManagement"
    ]))
  }
})

export default Web3FundManagement

const Web3FundManagementWrapper = styled.div`
  @media (${theme.lg}) {
    & ${BGWrapper} {
      height: 130%;
    }

    & ${ButtonWrapper} {
      flex-direction: row;
    }
  }

  & ${CardWrapper} {
    max-width: 80vw;
  }

  & ${ProductCardWrapper} {
    padding: 48px;
  }

  & ${SubDescLayoutWrapper} {
    @media (${theme.md}) {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 1em;
    }
  }

  & ${SubDescWrapper} {
    padding: 1em;
  }

  & ${SubDescContent} {
    width: initial;
    @media (${theme.lg}) {
      display: initial;
    }
  }
`
